import { PageController } from '@client/components/_core/layout/PageController';
import { EntityDetailsHeader } from '@client/components/_ui/data-display/EntityDetailsHeader';
import { Button } from '@client/components/_ui/data-entry/ButtonModern';
import { StatusBadge } from '@client/components/_ui/display/StatusBadge';
import { InlineQueryError } from '@client/components/_ui/feedback/InlineQueryError';
import { Loading } from '@client/components/_ui/feedback/Loading';
import { BackLink } from '@client/components/_ui/navigation/BackLink';
import { ClaimStatesBar } from '@client/components/claims/ClaimStatesBar';
import { ClaimStatusTag } from '@client/components/claims/ClaimStatusTag';
import { RepairJourneyView } from '@client/components/claims/vehicle/RepairJourney';
import { useTrpc } from '@client/hooks/api/useTrpc';
import { useOrgId } from '@client/hooks/business/org/useOrgId';
import { useFormatter } from '@client/hooks/utils/useFormatter';
import type { RouterOutputs } from '@client/trpc/client';
import { VEHICLE_ASSESSMENT_STATUS_LABELS, VEHICLE_INSPECTION_TRIAGE_LABELS } from '@shared/constants';
import { createFileRoute } from '@tanstack/react-router';
import { Descriptions, Divider, Empty, Image, Table, Tag, Typography } from 'antd';
import { Car, Crosshair, FileText } from 'lucide-react';
import { match } from 'ts-pattern';
import { claimRef } from '../portal-utils';

/** Shown in the journey's damage-capture stage when the tenant withholds the scan. */
const DAMAGE_CAPTURE_WITHHELD = 'Damage capture detail is not shown in the portal.';

export const Route = createFileRoute('/console/$orgId/fleet-portal/claims/details/$claimId')({
    staticData: {
        breadcrumb: {
            title: 'Claim details',
        },
    },
    params: {
        parse({ claimId }) {
            return { claimId: BigInt(claimId) };
        },
    },
    component: RouteComponent,
});

type ClaimDetail = RouterOutputs['fleetPortal']['getClaimDetail'];
type ClaimView = ClaimDetail['view'];
type Assessment = ClaimDetail['assessments'][number];
type Inspection = ClaimDetail['inspections'][number];
type Damage = Inspection['damages'][number];

/** Provider severity scale — mirrors the console vehicle tab labels. */
const SEVERITY_LABELS: Record<number, string> = {
    0: 'Insignificant',
    1: 'Small',
    2: 'Medium',
    3: 'Large',
    4: 'Replacement required',
};

function severityColour(severity: number | null): string {
    return match(severity)
        .with(0, 1, () => 'green')
        .with(2, () => 'gold')
        .with(3, () => 'orange')
        .with(4, () => 'red')
        .otherwise(() => 'default');
}

function assessmentStatusColour(status: Assessment['status']): string {
    return match(status)
        .with('REQUEST_SENT', () => 'blue')
        .with('CREATED', () => 'gold')
        .with('CLOSED', () => 'green')
        .exhaustive();
}

function triageTag(triage: number | null) {
    if (triage === null || VEHICLE_INSPECTION_TRIAGE_LABELS[triage] === undefined) {
        return null;
    }
    const colour = match(triage)
        .with(0, () => 'red')
        .with(1, () => 'green')
        .otherwise(() => 'orange');
    return <Tag color={colour}>Triage: {VEHICLE_INSPECTION_TRIAGE_LABELS[triage]}</Tag>;
}

function providerLabel(provider: string): string {
    return provider === 'RAVIN' ? 'Ravin AI' : provider;
}

/** 'left-front' → 'Left front'; keeps VIN capitalised. */
function humanise(value: string | null | undefined): string | undefined {
    const words = value?.replace(/[-_]+/g, ' ').trim();
    if (!words) {
        return undefined;
    }
    return (words.charAt(0).toUpperCase() + words.slice(1).toLowerCase()).replace(/\bvin\b/i, 'VIN');
}

/** Provider costs carry their own currency acronym (AUD on the AU cluster, EUR on the demo one). */
function formatDamageCost(cost: number, currency: string | null): string {
    try {
        return new Intl.NumberFormat('en-AU', {
            style: 'currency',
            currency: currency ?? 'AUD',
            maximumFractionDigits: Number.isInteger(cost) ? 0 : 2,
        }).format(cost);
    } catch {
        return [cost.toString(), currency].filter(Boolean).join(' ');
    }
}

function RouteComponent() {
    return (
        <PageController route={Route}>
            <ClaimDetailContent />
        </PageController>
    );
}

function ClaimDetailContent() {
    const orgId = useOrgId();
    const { claimId } = Route.useParams();
    const { trpc } = useTrpc();

    const query = trpc.fleetPortal.getClaimDetail.useQuery({ claimId }, { retry: 1, staleTime: 30_000 });

    if (query.isLoading) {
        return <Loading />;
    }
    if (query.isError) {
        return (
            <div className="flex flex-col gap-4">
                <BackLink to="/console/$orgId/fleet-portal/claims" params={{ orgId }}>
                    Back to all claims
                </BackLink>
                <InlineQueryError
                    error={query.error}
                    onRetry={() => void query.refetch()}
                    title="We couldn't load this claim right now."
                />
            </div>
        );
    }
    if (!query.data) {
        return null;
    }
    return <StripListClaimPage claim={query.data} view={query.data.view} />;
}

/**
 * The claim page built to the strip list (CUR-1050 / CUR-1069): the console
 * claim view with everything a fleet manager has no business in removed.
 * Which sections render is the tenant's configuration, applied server-side;
 * this component only lays out what arrived. Read-only by construction —
 * nothing here mutates.
 */
function StripListClaimPage({ claim, view }: { claim: ClaimDetail; view: ClaimView }) {
    const orgId = useOrgId();
    const { formatDate, formatCurrency } = useFormatter();
    const details = view.details;
    const vehicleLine = [claim.registration, claim.vehicle].filter(Boolean).join(' · ');

    return (
        <div className="flex flex-col gap-6">
            <div className="flex flex-col gap-2">
                <BackLink to="/console/$orgId/fleet-portal/claims" params={{ orgId }}>
                    Back to all claims
                </BackLink>
                <EntityDetailsHeader
                    className="mb-0"
                    title={`Claim ${claimRef(claim)}`}
                    titleAddon={
                        <>
                            <ClaimStatusTag
                                variant="outlined"
                                data={{ name: claim.statusName, displayColor: claim.statusDisplayColor }}
                            />
                            {claim.createdAt && (
                                <span className="text-muted-foreground text-sm">
                                    Lodged {formatDate(claim.createdAt)}
                                </span>
                            )}
                            {details?.claimOccurredAt && (
                                <span className="text-muted-foreground text-sm">
                                    Loss {formatDate(details.claimOccurredAt)}
                                </span>
                            )}
                        </>
                    }
                    mainInfo={[
                        { title: 'Vehicle', children: vehicleLine || '—' },
                        ...(details
                            ? [
                                  { title: 'Driver', children: details.driver ?? '—' },
                                  { title: 'Repairer', children: details.repairerName ?? '—' },
                                  { title: 'Product type', children: details.productTypeName ?? '—' },
                              ]
                            : []),
                    ]}
                />
            </div>

            {view.states && (
                <ClaimStatesBar
                    claimId={claim.id}
                    statesSnapshot={view.states.statesSnapshot}
                    currentStatePosition={view.states.currentStatePosition}
                    statesCompletedAt={view.states.statesCompletedAt}
                    isClaimClosed={claim.statusType === 'CLOSED'}
                    canAdvance={false}
                />
            )}

            {details && (
                <section>
                    <Divider titlePlacement="start" className="mt-0 mb-4 text-sm text-subtle-foreground">
                        Claim details
                    </Divider>
                    <Descriptions
                        column={{ xs: 1, sm: 2, lg: 4 }}
                        bordered
                        size="small"
                        items={[
                            { key: 'claim', label: 'Claim ID', children: claimRef(claim) },
                            {
                                key: 'lodged',
                                label: 'Lodged',
                                children: claim.createdAt ? formatDate(claim.createdAt) : '—',
                            },
                            { key: 'registration', label: 'Rego', children: claim.registration ?? '—' },
                            { key: 'vehicle', label: 'Vehicle', children: claim.vehicle ?? '—' },
                            { key: 'driver', label: 'Driver', children: details.driver ?? '—' },
                            { key: 'incidentType', label: 'Incident type', children: details.incidentType ?? '—' },
                            {
                                key: 'incidentDescription',
                                label: 'Incident description',
                                span: 2,
                                children: details.incidentDescription ?? '—',
                            },
                            {
                                key: 'excess',
                                label: 'Excess payable',
                                children:
                                    details.excessPayable === null
                                        ? '—'
                                        : formatCurrency(Number(details.excessPayable)),
                            },
                            { key: 'repairer', label: 'Repairer', children: details.repairerName ?? '—' },
                            { key: 'assessor', label: 'Assessor', children: details.assessorName ?? '—' },
                            {
                                key: 'estimate',
                                label: details.estimate?.basis === 'ASSESSED' ? 'Assessed total' : 'Estimate',
                                children: details.estimate
                                    ? `${formatCurrency(Number(details.estimate.amount))} ex GST`
                                    : '—',
                            },
                            {
                                key: 'tor',
                                label: 'Est. time off road',
                                children:
                                    details.estimatedDaysOffRoad === null
                                        ? '—'
                                        : `${details.estimatedDaysOffRoad} ${details.estimatedDaysOffRoad === 1 ? 'day' : 'days'}`,
                            },
                            {
                                key: 'offRoad',
                                label: 'Days off road',
                                children: <OffRoadCell offRoad={claim.offRoad} isDrivable={claim.isDrivable} />,
                            },
                            {
                                key: 'closed',
                                label: 'Closed',
                                children: claim.dtClosed ? formatDate(claim.dtClosed) : '—',
                            },
                        ]}
                    />
                </section>
            )}

            {view.repairProgress && (
                <section>
                    <Divider titlePlacement="start" className="mt-0 mb-4 text-sm text-subtle-foreground">
                        Repair progression
                    </Divider>
                    <RepairJourneyView
                        progress={view.repairProgress}
                        damageCaptureBody={
                            view.sections.damageScan ? (
                                <DamageScanSection claim={claim} />
                            ) : (
                                // The stage keeps its place in the journey; only its
                                // detail is withheld by the tenant's portal settings.
                                <Typography.Text type="secondary">{DAMAGE_CAPTURE_WITHHELD}</Typography.Text>
                            )
                        }
                    />
                </section>
            )}

            {view.sections.repairAssessment && (
                <section>
                    <Divider titlePlacement="start" className="mt-0 mb-4 text-sm text-subtle-foreground">
                        Repair assessment
                    </Divider>
                    <RepairAssessmentSection claim={claim} />
                </section>
            )}

            {view.sections.damageScan && !view.repairProgress && (
                <section>
                    <Divider titlePlacement="start" className="mt-0 mb-4 text-sm text-subtle-foreground">
                        AI damage scan
                    </Divider>
                    <DamageScanSection claim={claim} />
                </section>
            )}
        </div>
    );
}

/** Keep a positive driveability answer distinct from unknown, including an unknown duration after closure. */
function OffRoadCell({ offRoad, isDrivable }: Pick<ClaimDetail, 'offRoad' | 'isDrivable'>) {
    if (offRoad === null) {
        return <span>{isDrivable === true ? 'Driveable' : 'Unknown'}</span>;
    }
    return (
        <span>
            {offRoad.days === null ? 'Unknown duration' : `${offRoad.days} ${offRoad.days === 1 ? 'day' : 'days'}`}
            {offRoad.isTotalLoss && ' · total loss'}
            {offRoad.isBackOnRoad && !offRoad.isTotalLoss && ' · back on road'}
        </span>
    );
}

function RepairAssessmentSection({ claim }: { claim: ClaimDetail }) {
    const { formatDate, formatCurrency } = useFormatter();

    if (claim.assessments.length === 0 && claim.assessmentReports.length === 0) {
        return <Empty description="No repair assessment is available for this claim yet." />;
    }

    return (
        <div className="flex flex-col gap-4">
            {claim.assessments.map((assessment) => (
                <div
                    key={assessment.id.toString()}
                    className="flex flex-col gap-4 rounded-lg border border-separator bg-card p-4"
                >
                    <div className="flex flex-wrap items-center gap-2">
                        <Tag variant="outlined" color={assessmentStatusColour(assessment.status)}>
                            {VEHICLE_ASSESSMENT_STATUS_LABELS[assessment.status]}
                        </Tag>
                    </div>
                    <Descriptions
                        size="small"
                        column={{ xs: 1, sm: 3 }}
                        items={[
                            {
                                key: 'repairer',
                                label: 'Repairer',
                                children: assessment.repairerName ?? '—',
                            },
                            {
                                key: 'requested',
                                label: 'Requested',
                                children: formatDate(assessment.requestedAt),
                            },
                            {
                                key: 'updated',
                                label: 'Last updated',
                                children: assessment.lastSyncedAt ? formatDate(assessment.lastSyncedAt) : 'Not yet',
                            },
                        ]}
                    />
                    {assessment.items.length > 0 ? (
                        <Table<Assessment['items'][number]>
                            size="small"
                            rowKey={(item, index) => `${item.supplementaryNumber ?? index}`}
                            pagination={false}
                            dataSource={assessment.items}
                            columns={[
                                {
                                    title: 'Supplementary',
                                    dataIndex: 'supplementaryNumber',
                                    render: (value: number | null) =>
                                        value === null ? '—' : value === 0 ? 'Original' : value,
                                },
                                {
                                    title: 'Status',
                                    dataIndex: 'statusDescription',
                                    render: (value: string | null, item) => (
                                        <span className="flex items-center gap-2">
                                            {value ?? '—'}
                                            {item.isTotalLoss && (
                                                <Tag variant="outlined" color="red">
                                                    Total loss
                                                </Tag>
                                            )}
                                        </span>
                                    ),
                                },
                                {
                                    title: 'Estimated (ex tax)',
                                    dataIndex: 'estimatedTotalExTax',
                                    align: 'right' as const,
                                    render: (value: number | null) => (value === null ? '—' : formatCurrency(value)),
                                },
                                {
                                    title: 'Assessed (ex tax)',
                                    dataIndex: 'assessedTotalExTax',
                                    align: 'right' as const,
                                    render: (value: number | null) => (value === null ? '—' : formatCurrency(value)),
                                },
                            ]}
                        />
                    ) : (
                        <Typography.Text type="secondary">
                            No assessment data is available yet — it appears here once the repairer quotes and the
                            assessor reviews.
                        </Typography.Text>
                    )}
                </div>
            ))}

            {claim.assessmentReports.length > 0 && (
                <div className="flex flex-wrap gap-2">
                    {claim.assessmentReports.map((file) => (
                        <Button
                            key={file.id}
                            icon={<FileText />}
                            onClick={() => window.open(file.url, '_blank', 'noopener,noreferrer')}
                        >
                            {file.name}
                        </Button>
                    ))}
                </div>
            )}
        </div>
    );
}

function DamageScanSection({ claim }: { claim: ClaimDetail }) {
    if (claim.inspections.length === 0) {
        return <Empty description="No AI damage scan is available for this claim yet." />;
    }
    return (
        <div className="flex flex-col gap-4">
            {claim.inspections.map((inspection) => (
                <InspectionCard key={inspection.id.toString()} inspection={inspection} />
            ))}
        </div>
    );
}

function VehicleBandItem({ label, children }: { label: string; children: React.ReactNode }) {
    return (
        <div className="flex flex-col gap-0.5 border-separator border-l px-6 first:border-l-0 first:pl-0">
            <span className="font-semibold text-subtle-foreground text-xs uppercase tracking-wide">{label}</span>
            <span className="font-medium text-sm">{children}</span>
        </div>
    );
}

function InspectionCard({ inspection }: { inspection: Inspection }) {
    const { formatDate } = useFormatter();
    const vehicleTitle = [inspection.year, inspection.make, inspection.model].filter(Boolean).join(' ');
    const hasVehicleBand = Boolean(inspection.registrationNumber || inspection.vin || inspection.odometer);

    return (
        <div className="overflow-hidden rounded-lg border border-separator bg-card">
            <div className="flex flex-wrap items-center justify-between gap-x-4 gap-y-2 border-separator border-b bg-card-header-bg px-4 py-3">
                <div className="flex flex-wrap items-center gap-2">
                    <StatusBadge status={inspection.status} module="vehicleInspection" variant="outlined" />
                    {triageTag(inspection.triage)}
                    <Tag>{providerLabel(inspection.provider)}</Tag>
                </div>
                <div className="text-subtle-foreground text-xs">
                    Scan requested{' '}
                    <span className="font-medium text-muted-foreground">
                        {formatDate(new Date(inspection.requestedAt))}
                    </span>{' '}
                    · Report received{' '}
                    <span className="font-medium text-muted-foreground">
                        {inspection.reportReceivedAt ? formatDate(new Date(inspection.reportReceivedAt)) : 'Not yet'}
                    </span>
                </div>
            </div>

            {(vehicleTitle || inspection.impactAreas.length > 0) && (
                <div className="flex flex-wrap items-center justify-between gap-x-4 gap-y-2 px-4 pt-3 pb-1">
                    {vehicleTitle && (
                        <div className="flex items-center gap-2.5">
                            <span className="flex size-7 items-center justify-center rounded-lg bg-accent-500/15 text-accent-500">
                                <Car size={20} />
                            </span>
                            <span className="font-semibold text-base">{vehicleTitle}</span>
                        </div>
                    )}
                    {inspection.impactAreas.length > 0 && (
                        <div className="flex flex-wrap items-center gap-1.5">
                            <span className="flex items-center gap-1 font-semibold text-subtle-foreground text-xs uppercase tracking-wide">
                                <Crosshair size={14} /> Impact areas
                            </span>
                            {inspection.impactAreas.map((area) => (
                                <Tag
                                    key={area}
                                    className="border-warning-border bg-warning-subtle text-warning-foreground"
                                >
                                    {humanise(area)}
                                </Tag>
                            ))}
                        </div>
                    )}
                </div>
            )}

            {(hasVehicleBand || inspection.estimate || inspection.reportUrl) && (
                <div className="flex flex-wrap gap-y-3 border-separator border-b px-4 py-3">
                    <VehicleBandItem label="Registration">
                        {inspection.registrationNumber ? (
                            <span className="font-mono">{inspection.registrationNumber}</span>
                        ) : (
                            '—'
                        )}
                    </VehicleBandItem>
                    <VehicleBandItem label="VIN">
                        {inspection.vin ? <span className="font-mono">{inspection.vin}</span> : '—'}
                    </VehicleBandItem>
                    {inspection.odometer && <VehicleBandItem label="Odometer">{inspection.odometer}</VehicleBandItem>}
                    {inspection.estimate && (
                        <VehicleBandItem label="Estimated repair cost">
                            {formatDamageCost(inspection.estimate.total, inspection.estimate.currency)}{' '}
                            <span className="font-normal text-subtle-foreground">est.</span>
                        </VehicleBandItem>
                    )}
                    {inspection.reportUrl && (
                        <VehicleBandItem label="Assessment report">
                            <Button
                                type="link"
                                size="small"
                                icon={<FileText />}
                                className="-ml-2"
                                onClick={() =>
                                    inspection.reportUrl &&
                                    window.open(inspection.reportUrl, '_blank', 'noopener,noreferrer')
                                }
                            >
                                Open PDF report
                            </Button>
                        </VehicleBandItem>
                    )}
                </div>
            )}

            {inspection.damages.length > 0 ? (
                <DamagesTable damages={inspection.damages} />
            ) : (
                <div className="px-4 py-3">
                    <Typography.Text type="secondary">
                        {inspection.status === 'LINK_REQUESTED'
                            ? 'The scan has not been completed yet — damages appear here once the report arrives.'
                            : 'The report contains no detected damages.'}
                    </Typography.Text>
                </div>
            )}

            {inspection.photos.length > 0 && (
                <div className="flex flex-col gap-2 border-separator border-t px-4 py-3">
                    <div className="flex items-center gap-2">
                        <span className="font-semibold text-sm">Photos</span>
                        <span className="text-subtle-foreground text-xs">
                            {inspection.photos.length} {inspection.photos.length === 1 ? 'photo' : 'photos'}
                        </span>
                    </div>
                    <Image.PreviewGroup items={inspection.photos.map((photo) => photo.url)}>
                        <div className="flex flex-wrap gap-2">
                            {inspection.photos.map((photo) => (
                                <Image
                                    key={photo.id}
                                    src={photo.url}
                                    alt={photo.name}
                                    width={96}
                                    height={72}
                                    className="rounded-md border border-separator object-cover"
                                />
                            ))}
                        </div>
                    </Image.PreviewGroup>
                </div>
            )}
        </div>
    );
}

function DamagesTable({ damages }: { damages: Damage[] }) {
    const hasCosts = damages.some((damage) => damage.cost !== null);
    return (
        <Table<Damage>
            size="small"
            rowKey={(_damage, index) => `${index}`}
            pagination={false}
            dataSource={damages}
            columns={[
                {
                    title: 'Part',
                    dataIndex: 'partName',
                    render: (_, row) => (
                        <div className="flex flex-col">
                            <span className="font-semibold">{humanise(row.partName) ?? '—'}</span>
                            {humanise(row.locationName) && (
                                <span className="text-subtle-foreground text-xs">{humanise(row.locationName)}</span>
                            )}
                        </div>
                    ),
                },
                {
                    title: 'Damage',
                    dataIndex: 'damageTypeName',
                    render: (_, row) => (
                        <div className="flex max-w-96 flex-col">
                            <span className="font-medium">{humanise(row.damageTypeName) ?? '—'}</span>
                            {row.damageDescription && (
                                <span className="text-subtle-foreground text-xs">{row.damageDescription}</span>
                            )}
                        </div>
                    ),
                },
                {
                    title: 'Severity',
                    dataIndex: 'severity',
                    width: 180,
                    render: (severity: number | null) =>
                        severity !== null && SEVERITY_LABELS[severity] !== undefined ? (
                            <Tag color={severityColour(severity)}>{SEVERITY_LABELS[severity]}</Tag>
                        ) : (
                            <Tag>Not rated</Tag>
                        ),
                },
                ...(hasCosts
                    ? [
                          {
                              title: 'Est. cost',
                              dataIndex: 'cost' as const,
                              width: 110,
                              align: 'right' as const,
                              render: (_: unknown, row: Damage) =>
                                  row.cost !== null ? (
                                      <span className="tabular-nums">{formatDamageCost(row.cost, row.currency)}</span>
                                  ) : (
                                      <span className="text-subtle-foreground">—</span>
                                  ),
                          },
                      ]
                    : []),
                {
                    title: 'Pre-existing',
                    dataIndex: 'isExisting',
                    width: 100,
                    render: (isExisting: boolean | null) => (isExisting === null ? '—' : isExisting ? 'Yes' : 'No'),
                },
            ]}
        />
    );
}
